import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, IconCircle, Button, BlobField, ChildAvatar } from "../components/ui";
import { QUICK_CARDS, greeting } from "../data/constants";
import { familyService } from "../services/familyService";
import { useFamilyAuth } from "../store/familyAuthStore";
import { childAge, childDays } from "../utils/format";

export default function Dashboard() {
  const navigate = useNavigate();
  const family = useFamilyAuth((s) => s.family) || {};
  const [children, setChildren] = useState([]);
  const [forms, setForms] = useState([]);

  useEffect(() => {
    familyService.getChildren().then((r) => setChildren(r?.data || [])).catch(() => {});
    familyService.getForms().then((r) => setForms(r?.data || [])).catch(() => {});
  }, []);

  const mostUsed = forms.slice(0, 4);
  const names = children.map((c) => c.first_name).join(" and ");
  const centreName = family.centre_name || "your centre";

  return (
    <div>
      <section className="dash-hero on-dark">
        <BlobField />
        <div className="dash-hero-inner">
          <p className="eyebrow on-dark">{greeting()}</p>
          <h1 className="display dash-title">
            Hello, {family.full_name || "Stride"} family
          </h1>
          <p className="dash-lead">
            {children.length
              ? `Everything for ${names}'s days at Stride — all in one place, whenever you need it.`
              : "Everything for your family's days at Stride — all in one place, whenever you need it."}
          </p>

          {children.length > 0 && (
            <div className="child-chips">
              {children.map((c, i) => (
                <div className="child-card" key={c.id}>
                  <ChildAvatar c={{ name: c.first_name, index: i }} />
                  <div>
                    <div className="cc-name">{c.first_name} <span className="cc-age">· {childAge(c.dob)}</span></div>
                    <div className="cc-meta">{[c.room, childDays(c.days)].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="page">
        <p className="eyebrow eyebrow--gap">Quick access</p>
        <div className="quick-grid">
          {QUICK_CARDS.map((q) => (
            <button className="card card-hover quick-card" key={q.id} onClick={() => navigate("/" + q.id)}>
              <IconCircle name={q.icon} size="lg" />
              <h3>{q.title}</h3>
              <p>{q.desc}</p>
              <span className="quick-go nav-label">Open <Icon name="chevronRight" className="ico-14" /></span>
            </button>
          ))}
        </div>

        <div className="dash-cols">
          <div>
            <div className="dash-row-head">
              <p className="eyebrow">Most-used forms</p>
              <a href="/forms" onClick={(e) => { e.preventDefault(); navigate("/forms"); }} className="nav-label">
                View all forms →
              </a>
            </div>
            <div className="card form-list-card">
              {mostUsed.map((f) => (
                <button key={f.code} className="form-row" onClick={() => navigate("/forms/" + f.code)}>
                  <span className="icon-circle soft md"><Icon name={f.icon || "forms"} /></span>
                  <div className="form-row-main">
                    <div className="form-row-title">{f.name}</div>
                    <div className="form-row-sub">{(f.description || "").split(".")[0]}.</div>
                  </div>
                  <span className="form-row-time"><Icon name="clock" className="ico-13" /> {f.est_time}</span>
                  <Icon name="chevronRight" className="ico-18 ico-soft" />
                </button>
              ))}
              {mostUsed.length === 0 && (
                <p className="muted empty-pad">No forms available yet.</p>
              )}
            </div>
          </div>

          <aside>
            <p className="eyebrow eyebrow--gap">Your centre</p>
            <div className="card help-card">
              <div className="help-centre">
                <span className="icon-circle"><Icon name="pin" /></span>
                <div>
                  <div className="help-centre-name">{centreName}</div>
                  <div className="help-centre-sub">{family.centre_hours || "6:30am – 6:30pm, Mon–Fri"}</div>
                </div>
              </div>
              <hr className="divider" />
              <ul className="help-list">
                {family.centre_phone && <li><Icon name="phone" className="ico-16" /> {family.centre_phone}</li>}
                {family.centre_email && <li><Icon name="mail" className="ico-16" /> {family.centre_email}</li>}
                {family.centre_manager && <li><Icon name="user" className="ico-16" /> {family.centre_manager}, Director</li>}
              </ul>
              <Button variant="ghost" block size="sm" className="mt-18" onClick={() => navigate("/handbook")}>
                Read the parent handbook
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
